import { useState } from 'react';
import { motion } from 'framer-motion';
import { Target, MapPin, Tag, DollarSign, AlertCircle, Loader2, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${window.location.origin}/api`;
const MELANIN_ICON = "https://customer-assets.emergentagent.com/job_blackownedhub/artifacts/9kb68tzi_Melanin%20Icon%20-%20White%20with%20Black%20Background.png";

const CATEGORIES = [
  "Restaurants",
  "Retail",
  "Services",
  "Beauty & Wellness",
  "Professional Services",
  "Health & Medical",
  "Entertainment",
  "Automotive",
  "Arts & Culture",
  "Real Estate & Housing",
  "Travel & Lodging",
  "Media & Creative Agencies",
  "Community & Non-Profit",
  "Home & Construction",
  "Manufacturing & Wholesale",
  "Technology",
  "Education",
  "Other"
];

export default function CreateBountyDialog({ open, onOpenChange, walletBalance, onBountyCreated }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    city: '',
    state: '',
    reward_amount: '',
    requires_approval: false,
    expires_in_days: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const reward = parseFloat(formData.reward_amount);
    
    if (!formData.title.trim()) {
      toast.error('Please enter a bounty title');
      return;
    }
    
    if (!reward || reward < 1) {
      toast.error('Minimum reward is 1 MEL');
      return;
    }
    
    if (reward > walletBalance) {
      toast.error('Insufficient MEL balance');
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/bounties`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: formData.title.trim(),
          description: formData.description.trim() || null,
          category: formData.category || null,
          city: formData.city.trim() || null,
          state: formData.state.trim().toUpperCase() || null,
          reward_amount: reward,
          requires_approval: formData.requires_approval,
          expires_in_days: formData.expires_in_days && formData.expires_in_days !== 'none' ? parseInt(formData.expires_in_days) : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create bounty');
      }

      toast.success('Bounty created! Your MEL has been escrowed.');
      onOpenChange(false);
      onBountyCreated?.();
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        category: '',
        city: '',
        state: '',
        reward_amount: '',
        requires_approval: false,
        expires_in_days: ''
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const reward = parseFloat(formData.reward_amount) || 0;
  const canSubmit = formData.title.trim() && reward >= 1 && reward <= walletBalance;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-green-400" />
            Create a Bounty
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Offer a reward for someone to find and submit a business that matches your criteria.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Wallet Balance */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <span className="text-zinc-400 text-sm">Your Balance</span>
            <div className="flex items-center gap-2">
              <img src={MELANIN_ICON} alt="" className="w-4 h-4" />
              <span className="font-bold text-white">{walletBalance.toFixed(2)}</span>
              <span className="text-zinc-500 text-sm">MEL</span>
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">Bounty Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Find a Black-owned plumber in Sumter"
              className="bg-zinc-900 border-zinc-800 text-white"
              data-testid="bounty-title-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">Description (Optional)</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add more details about what you're looking for..."
              className="bg-zinc-900 border-zinc-800 text-white min-h-[80px]"
              data-testid="bounty-description-input"
            />
          </div>

          {/* Category & Location */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white" data-testid="bounty-category">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F0F0F] border-zinc-800">
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat} className="text-white hover:bg-zinc-800">
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">City</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Atlanta"
                className="bg-zinc-900 border-zinc-800 text-white"
                data-testid="bounty-city-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">State</Label>
              <Input
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                placeholder="GA"
                maxLength={2}
                className="bg-zinc-900 border-zinc-800 text-white"
                data-testid="bounty-state-input"
              />
            </div>
          </div>

          {/* Reward Amount */}
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">Reward Amount *</Label>
            <div className="relative">
              <img src={MELANIN_ICON} alt="" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" />
              <Input
                type="number"
                value={formData.reward_amount}
                onChange={(e) => setFormData({ ...formData, reward_amount: e.target.value })}
                placeholder="e.g., 50"
                min={1}
                step={0.01}
                className="bg-zinc-900 border-zinc-800 text-white pl-10"
                data-testid="bounty-reward-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">MEL</span>
            </div>
            {reward > 0 && reward < 1 && (
              <p className="text-red-400 text-xs">Minimum reward is 1 MEL</p>
            )}
            {reward > walletBalance && (
              <p className="text-red-400 text-xs">Insufficient balance</p>
            )}
          </div>

          {/* Expiration */}
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm">Expires In (Optional)</Label>
            <Select 
              value={formData.expires_in_days} 
              onValueChange={(value) => setFormData({ ...formData, expires_in_days: value })}
            >
              <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white" data-testid="bounty-expiration">
                <SelectValue placeholder="No expiration" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-zinc-800">
                <SelectItem value="none" className="text-white hover:bg-zinc-800">No expiration</SelectItem>
                <SelectItem value="7" className="text-white hover:bg-zinc-800">7 days</SelectItem>
                <SelectItem value="14" className="text-white hover:bg-zinc-800">14 days</SelectItem>
                <SelectItem value="30" className="text-white hover:bg-zinc-800">30 days</SelectItem>
                <SelectItem value="60" className="text-white hover:bg-zinc-800">60 days</SelectItem>
                <SelectItem value="90" className="text-white hover:bg-zinc-800">90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Approval Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-zinc-500 mt-0.5" />
              <div>
                <p className="text-white font-medium text-sm">Require My Approval</p>
                <p className="text-zinc-500 text-xs">
                  Review and approve claims before MEL is transferred
                </p>
              </div>
            </div>
            <Switch
              checked={formData.requires_approval}
              onCheckedChange={(checked) => setFormData({ ...formData, requires_approval: checked })}
              data-testid="bounty-approval-toggle"
            />
          </div>

          {/* Info Box */}
          <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5" />
              <p className="text-yellow-400/80 text-xs">
                Your MEL will be escrowed (locked) when you create this bounty. 
                It will be transferred to the person who successfully fulfills it, 
                or refunded if you cancel (when no active claims exist).
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="w-full py-3 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold hover:from-green-500 hover:to-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="submit-bounty-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating Bounty...
              </>
            ) : (
              <>
                <Target className="w-5 h-5" />
                Create Bounty ({reward > 0 ? reward.toFixed(2) : '0'} MEL)
              </>
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
